/* eslint-disable react-refresh/only-export-components */
import { createContext, ReactNode, useCallback, useEffect, useState } from 'react'
import { browserName, browserVersion, deviceType, osName, osVersion } from 'react-device-detect'
import toast from 'react-hot-toast'
import { useLocation } from 'react-router-dom'

import { useAppSelector } from '../hooks/useAppSelector'
import { useDialogBox } from '../hooks/useDialogBox'
import { useGeolocation } from '../hooks/useGeolocation'
import { useLocalStorage } from '../hooks/useLocalStorage'
import {
  checkExistingOpenPromoterStayingTimeRegister,
  closePromoterStayingTime,
  ClosePromoterStayingTimeRegisterRequest,
  createPromoterStayingTime,
  updatePromoterStayingTimeDevice,
} from '../services/reportServices'
import { PROMOTER_STAYING_TIME_DATA } from '../utils/constants'

interface PromoterStayingTimeCycle {
  id?: string
  entryTime: Date
  entryTimeCoordinates: string
  leaveTime?: Date
  leaveTimeCoordinates?: string
  device?: string
  reportId: string
  userId: string
}

type PromotersStayingTimeContextDataProps = {
  activeCycle: PromoterStayingTimeCycle | null
  elapsedTime: number
  startNewCycle: (reportId: string) => Promise<void>
  endCurrentCycle: () => Promise<void>
  isLoadingGeolocation: boolean
}

type PromotersStayingTimeContextProviderProps = {
  children: ReactNode
}

export const PromotersStayingTimeContext = createContext<PromotersStayingTimeContextDataProps>(
  {} as PromotersStayingTimeContextDataProps,
)

export function PromotersStayingTimeProvider({ children }: PromotersStayingTimeContextProviderProps) {
  const [activeCycle, setActiveCycle] = useState<PromoterStayingTimeCycle | null>(null)
  const [elapsedTime, setElapsedTime] = useState(0)
  const [isLoadingGeolocation, setIsLoadingGeolocation] = useState(false)

  const { showDialog, setIsLoadingDialogAction } = useDialogBox()
  const { profile } = useAppSelector(state => state.auth)

  const [currentCycle, setCurrentCycle, removeCurrentCycle] = useLocalStorage(PROMOTER_STAYING_TIME_DATA)
  const getGeolocation = useGeolocation()

  const { pathname } = useLocation()

  async function startNewCycle(reportId: string) {
    const currentTime = new Date()

    const currentHour = currentTime.getHours()
    if (currentHour >= 21) {
      return showDialog({
        title: 'Não é possível registrar entada depois das 21 horas',
        type: 'info',
      })
    }

    const openedRegister = await checkExistingOpenPromoterStayingTimeRegister()
    if (openedRegister) {
      const currentDevice = `${browserName}:${browserVersion}:${deviceType}:${osName}:${osVersion}`
      if (openedRegister.device === currentDevice) {
        const foundedCycle: Exclude<PromoterStayingTimeCycle, 'leaveTime' | 'leaveTimeCoordinates'> = {
          id: openedRegister.id,
          entryTime: new Date(openedRegister.entry_time),
          entryTimeCoordinates: openedRegister.entry_time_coordinates,
          reportId: openedRegister.report_id,
          userId: openedRegister.promoter_id,
          device: currentDevice,
        }
        setActiveCycle(foundedCycle)
        setCurrentCycle(JSON.stringify(foundedCycle))

        if (openedRegister.report_id !== reportId) {
          showDialog({
            title: 'Registro de entrada em aberto encontrado!',
            description: `Identificamos um registro de ponto em aberto para hoje no nosso sistema feito
              em outra loja. Encerre o registro atual para dar entrada nesta.`,
            type: 'info',
          })
        } else {
          showDialog({
            title: 'Registro de entrada em aberto encontrado!',
            description: `Identificamos um registro de ponto em aberto para hoje no nosso sistema e
              restauramos.`,
            type: 'info',
          })
        }
        return
      } else {
        let description = ''
        if (openedRegister.report_id !== reportId) {
          description = `Identificamos um registro de ponto em outra loja aberto para hoje no nosso sistema feito
            em outro dispositivo. Deseja restaurar o registro neste novo dispositivo? Você ainda precisará encerrar
            o registro atual para dar entrada nesta loja.`
        } else {
          description = `Identificamos um registro de ponto em aberto para hoje no nosso sistema feito
            em outro dispositivo. Deseja continuar o registro neste?`
        }
        showDialog({
          title: 'Registro de entrada em aberto encontrado!',
          description,
          showConfirmation: true,
          action: async () => {
            await updatePromoterStayingTimeDevice(openedRegister.id, currentDevice)
            const existingCycle: Exclude<PromoterStayingTimeCycle, 'leaveTime' | 'leaveTimeCoordinates'> = {
              id: openedRegister.id,
              entryTime: new Date(openedRegister.entry_time),
              entryTimeCoordinates: openedRegister.entry_time_coordinates,
              reportId: openedRegister.report_id,
              userId: openedRegister.promoter_id,
              device: currentDevice,
            }
            setActiveCycle(existingCycle)
            setCurrentCycle(JSON.stringify(existingCycle))
          },
          cancelButtonText: 'Cancelar',
        })
        return
      }
    }

    showDialog({
      title: 'Tem certeza que deseja registrar entrada nessa loja?',
      showConfirmation: true,
      action: async () => await registerNewCycle(reportId),
    })
  }

  async function registerNewCycle(reportId: string) {
    if (currentCycle) return

    setIsLoadingGeolocation(true)
    setIsLoadingDialogAction(true)
    const coordinates = await getGeolocation()
    setIsLoadingGeolocation(false)
    setIsLoadingDialogAction(false)

    if (!coordinates || !profile) return

    let newCycle: Exclude<PromoterStayingTimeCycle, 'leaveTime' | 'leaveTimeCoordinates'> = {
      entryTime: new Date(),
      entryTimeCoordinates: `${coordinates.latitude}:${coordinates.longitude}`,
      reportId,
      userId: profile.id,
      device: `${browserName}:${browserVersion}:${deviceType}:${osName}:${osVersion}`,
    }

    const result = await createPromoterStayingTime(newCycle)

    if (result) {
      newCycle = {
        ...newCycle,
        id: result.promoterStayingTime.id,
      }
      setActiveCycle(newCycle)
      setCurrentCycle(JSON.stringify(newCycle))
      setElapsedTime(0)
    }
  }

  async function endCurrentCycle() {
    if (!activeCycle) return

    setIsLoadingGeolocation(true)
    setIsLoadingDialogAction(true)
    const coordinates = await getGeolocation()
    setIsLoadingGeolocation(false)
    setIsLoadingDialogAction(false)
    if (!coordinates) return

    if (!activeCycle.id) {
      removeCurrentCycle()
      setActiveCycle(null)
      setElapsedTime(0)
      return
    }

    const newCycle: ClosePromoterStayingTimeRegisterRequest = {
      id: activeCycle.id,
      leaveTime: new Date(),
      leaveTimeCoordinates: `${coordinates.latitude}:${coordinates.longitude}`,
    }

    const result = await closePromoterStayingTime(newCycle)

    if (result) {
      toast.success('Registro salvo!', { position: 'top-left' })
    }
    removeCurrentCycle()
    setActiveCycle(null)
    setElapsedTime(0)
  }

  async function confirmTodoPromoterChecklistBeforeCloseSpot() {
    showDialog({
      title: 'Tem certeza que deseja registrar saída da loja?',
      content: (
        <div className="flex flex-col items-center">
          <p className="text-sm text-gray-500 mb-4">Antes de marcar saída da loja, verifique se já concluiu e registrou as fotos dos itens da lista:</p>
          <div className="flex flex-col justify-center items-center md:items-start w-4/5">
            <div className="flex items-center">
              <input type="checkbox" />
              <span className="text-md text-gray-500 ml-2">Foto da chegada ao PDV</span>
            </div>
            {new Date().getDay() === 1 && (
              <div className="flex items-center">
                <input type="checkbox" />
                <span className="text-md text-gray-500 ml-2">Relatório de estoque atualizado</span>
              </div>
            )}
            <div className="flex items-center">
              <input type="checkbox" />
              <span className="text-md text-gray-500 ml-2">Conservação das gôndolas</span>
            </div>
            <div className="flex items-center">
              <input type="checkbox" />
              <span className="text-md text-gray-500 ml-2">Rodízio das mercadorias</span>
            </div>
            <div className="flex items-center">
              <input type="checkbox" />
              <span className="text-md text-gray-500 ml-2">Abastecimento</span>
            </div>
            <div className="flex items-center">
              <input type="checkbox" />
              <span className="text-md text-gray-500 ml-2">Precificação</span>
            </div>
          </div>
        </div>
      ),
      action: endCurrentCycle,
      showConfirmation: true,
    })
  }

  const clearPromoterStayingTimeData = useCallback(() => {
    removeCurrentCycle()
    setActiveCycle(null)
    setElapsedTime(0)
  }, [removeCurrentCycle])

  const checkExistingOpenStayingTimeRegister = useCallback(async () => {
    if (!profile) return
    if ((profile.role === 'PROMOTER' || profile.subrole === 'PROMOTER' || profile.role === 'ROOT') && !pathname.includes('/temporary-access')) {
      const openedRegister = await checkExistingOpenPromoterStayingTimeRegister()

      const title = 'Registro de ponto encontrado! Deseja continuar nesse dispositivo?'
      const description = `Foi identificado que você registrou entrada nessa loja hoje por outro dispositivo.
        Só é permitido manter a contagem em um dispositivo. Deseja continuar nesse dispositivo?`

      if (openedRegister) {
        if (currentCycle) {
          const deviceCycle = JSON.parse(currentCycle) as PromoterStayingTimeCycle

          if (openedRegister.device !== deviceCycle.device) {
            showDialog({
              title: openedRegister.device
                ? title
                : 'Registro de entrada em loja sem dispositivo definido!',
              description: openedRegister.device
                ? description
                : 'Confirme para definir esse dispositivo como o dispositivo a ser usado hoje.',
              showConfirmation: true,
              action: async () => {
                const newDevice = `${browserName}:${browserVersion}:${deviceType}:${osName}:${osVersion}`
                await updatePromoterStayingTimeDevice(openedRegister.id, newDevice)
              },
              cancelButtonText: 'Cancelar',
              cancelAction: () => {
                clearPromoterStayingTimeData()
              },
            })
          }

          setActiveCycle({
            ...deviceCycle,
            entryTime: new Date(deviceCycle.entryTime),
          })
        } else {
          showDialog({
            title,
            description,
            showConfirmation: true,
            action: async () => {
              const newDevice = `${browserName}:${browserVersion}:${deviceType}:${osName}:${osVersion}`
              await updatePromoterStayingTimeDevice(openedRegister.id, newDevice)
              const existingCycle: Exclude<PromoterStayingTimeCycle, 'leaveTime' | 'leaveTimeCoordinates'> = {
                id: openedRegister.id,
                entryTime: new Date(openedRegister.entry_time),
                entryTimeCoordinates: openedRegister.entry_time_coordinates,
                reportId: openedRegister.report_id,
                userId: openedRegister.promoter_id,
                device: newDevice,
              }
              setActiveCycle(existingCycle)
              setCurrentCycle(JSON.stringify(existingCycle))
            },
            cancelButtonText: 'Cancelar',
            cancelAction: () => {
              clearPromoterStayingTimeData()
            },
          })
        }
      } else {
        if (currentCycle) {
          clearPromoterStayingTimeData()
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, showDialog, clearPromoterStayingTimeData, setCurrentCycle])

  useEffect(() => {
    checkExistingOpenStayingTimeRegister()
  }, [checkExistingOpenStayingTimeRegister])

  useEffect(() => {
    let interval: NodeJS.Timeout

    if (activeCycle) {
      interval = setInterval(() => {
        const now = new Date()
        const diffInSeconds = Math.floor((now.getTime() - activeCycle.entryTime.getTime()) / 1000)
        setElapsedTime(diffInSeconds)
      }, 1000)
    }

    return () => {
      clearInterval(interval)
    }
  }, [activeCycle])

  return (
    <PromotersStayingTimeContext.Provider
      value={{
        activeCycle,
        elapsedTime,
        startNewCycle,
        endCurrentCycle: confirmTodoPromoterChecklistBeforeCloseSpot,
        isLoadingGeolocation,
      }}
    >
      {children}
    </PromotersStayingTimeContext.Provider>
  )
}
